import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';
import { Field, Label, Switch } from '@headlessui/react';
import * as React from 'react';

const ToggleVariants = cva(
  `
  relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none border-2 border-transparent
  hover:border-primary-hover/50 `,
  {
    variants: {
      size: {
        sm: 'w-[36px] h-[20px]',
        md: 'w-[44px] h-[24px]'
      },
      variant: {
        defaultBackground: '',
        light: '',
        neutral: '',
        default: '',
        background: ''
      },
      IsEnabled: {
        true: '',
        false: ''
      }
    },
    compoundVariants: [
      //DefaultBackground on-off
      {
        IsEnabled: false,
        variant: 'defaultBackground',
        className: 'bg-background-input [&>span]:shadow-[0px_1px_5px_1px_theme(colors.black.100)]'
      },
      {
        IsEnabled: true,
        variant: 'defaultBackground',
        className: 'bg-primary-base [&>span]:shadow-[0px_1px_5px_1px_theme(colors.black.600)]'
      },
      //Default on-off
      {
        IsEnabled: false,
        variant: 'default',
        className: 'bg-background-muted  [&>span]:shadow-[0px_1px_5px_1px_theme(colors.black.100)]'
      },
      {
        IsEnabled: true,
        variant: 'default',
        className: 'bg-primary-base [&>span]:shadow-[0px_1px_5px_1px_theme(colors.black.600)]'
      },
      //background on-off
      {
        IsEnabled: false,
        variant: 'background',
        className: 'bg-background-input  [&>span]:shadow-[0px_1px_5px_1px_theme(colors.black.100)]'
      },
      {
        IsEnabled: true,
        variant: 'background',
        className: 'bg-background-input [&>span]:shadow-[0px_1px_5px_1px_theme(colors.black.100)]'
      },
      //outline
      {
        IsEnabled: false,
        variant: 'light',
        className: 'bg-background-muted  [&>span]:shadow-[0px_1px_5px_1px_theme(colors.black.100)]'
      },
      {
        IsEnabled: true,
        variant: 'light',
        className: 'bg-background-muted [&>span]:shadow-[0px_1px_5px_1px_theme(colors.black.200)]'
      }
    ],
    defaultVariants: {
      variant: 'default',
      IsEnabled: false,
      size: 'md'
    }
  }
);

type ToggleVariantProps = VariantProps<typeof ToggleVariants>;

export interface ToggleProps extends Omit<ToggleVariantProps, 'IsEnabled'> {
  /** Id of the rendered control, so a surrounding label's `htmlFor` resolves to it. */
  id?: string;
  labelText?: string;
  className?: string;
  /** Current checked state — the parent owns this value. */
  checked?: boolean;
  disabled?: boolean;
  /** Called with the next value when the user clicks the toggle. The parent is responsible for updating `checked`. */
  onChange?: (val: boolean) => void;
  testId?: string;
  /** Marks the control invalid. Injected by InputWrapper. */
  hasError?: boolean;
  /** Accessible name of the switch when no visible label names it. */
  ariaLabel?: string;
}

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      variant,
      size = 'md',
      labelText,
      onChange,
      checked = false,
      disabled,
      testId,
      className,
      hasError,
      ariaLabel,
      ...props
    },
    ref
  ) => {
    const handleChange = (nextChecked: boolean) => {
      onChange?.(nextChecked);
    };

    const thumbTranslate = {
      sm: checked ? 'translate-x-[17px]' : 'translate-x-[2px]',
      md: checked ? 'translate-x-[18px]' : 'translate-x-1'
    };
    const thumbSize = {
      sm: 'h-[14px] w-[14px]',
      md: 'h-[18px] w-[18px]'
    };

    return labelText ? (
      <Field className='inline-flex gap-sm'>
        <Switch
          ref={ref}
          disabled={disabled}
          checked={checked}
          data-testid={testId}
          aria-label={ariaLabel}
          aria-invalid={hasError || undefined}
          onChange={handleChange}
          className={cn(
            ToggleVariants({ variant, size, IsEnabled: checked }),
            disabled && 'opacity-50 hover:border-transparent!',
            className
          )}
          {...props}
        >
          <span
            className={cn(
              thumbTranslate[size!],
              thumbSize[size!],
              ' inline-block transform rounded-full bg-background-input transition-transform'
            )}
          ></span>
        </Switch>
        <Label>{labelText}</Label>
      </Field>
    ) : (
      <Switch
        ref={ref}
        checked={checked}
        disabled={disabled}
        data-testid={testId}
        aria-label={ariaLabel}
        aria-invalid={hasError || undefined}
        onChange={handleChange}
        className={cn(
          ToggleVariants({ variant, size, IsEnabled: checked }),
          disabled && 'opacity-50 hover:border-transparent!',
          className
        )}
        {...props}
      >
        <span
          className={cn(
            thumbTranslate[size!],
            thumbSize[size!],
            ' inline-block transform rounded-full bg-background-input transition-transform'
          )}
        ></span>
      </Switch>
    );
  }
);

Toggle.displayName = 'Toggle';
